// The primary navigation, rendered from `nav.ts`.
//
// It has one appearance — icon and label — and two fates: it is here, or it is
// gone. Collapsing removes the column outright rather than narrowing it to a
// strip of icons, which means there is no second, unlabelled rendering of this
// list to drift out of step with the first. What is left when it goes is the
// favourites rail, which carries the control that brings it back.
//
// On a phone this is the drawer: same list, over the page, closed by the
// backdrop, by Escape, or by arriving somewhere.

import { useEffect, useRef } from "react";
import { Link, NavLink } from "react-router-dom";
import { BrandMark } from "../components/BrandMark";
import { COMPANY } from "../lib/company";
import { CollapseIcon } from "./icons";
import { NAV_FOOTER, visibleGroups, type NavContext, type NavItem } from "./nav";
import { DASHBOARD_ROUTE } from "../dashboard";
import { formatLimit, limitOf, usedOf } from "../lib/entitlements";
import type { Entitlements } from "../lib/types";

function Row({ item }: { item: NavItem }) {
  const Icon = item.icon;
  return (
    <li>
      <NavLink
        to={item.route}
        className={({ isActive }) => (isActive ? "nav-row on" : "nav-row")}
        // The disclosure `nav.ts` requires every item to carry. The label is
        // always visible beside it, so this is a description rather than the
        // accessible name.
        title={item.title}
      >
        <Icon className="nav-icon" />
        <span className="nav-label">{item.label}</span>
      </NavLink>
    </li>
  );
}

/** Put the panel away. One definition, because it appears in two places. */
function CollapseButton({ drawer, onClose }: { drawer: boolean; onClose: () => void }) {
  const label = drawer ? "Close menu" : "Hide navigation";
  return (
    <button type="button" className="sidebar-collapse" onClick={onClose}
            aria-label={label} title={label}
            aria-expanded={true} aria-controls="app-sidebar">
      <CollapseIcon className="nav-icon" />
    </button>
  );
}

interface Props {
  ctx: NavContext;
  /** Phone only: the sidebar is over the page rather than beside it. */
  drawer: boolean;
  onNavigate: () => void;
  /** Put the panel away. The same state the rail's and the header's toggles
   *  drive — this is a third door onto it, on the panel being closed. */
  onClose: () => void;
  entitlements: Entitlements | null;
  plan: string | null;
}

export function CollapsibleSidebar({
  ctx, drawer, onNavigate, onClose, entitlements, plan,
}: Props) {
  const groups = visibleGroups(ctx);
  const ref = useRef<HTMLElement>(null);

  // A drawer that cannot be closed with the keyboard is a trap, and this one
  // covers the whole page.
  useEffect(() => {
    if (!drawer) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    ref.current?.focus();
    return () => window.removeEventListener("keydown", onKey);
  }, [drawer, onClose]);

  // The month's posts, as the plan meters them. Shown only when there is a
  // limit to show: on an unlimited plan a bar that is always empty says
  // nothing, and on a plan with no quota feature at all there is no number.
  //
  // Kept deliberately small. It sits below the scrolling list, so every pixel
  // it takes is a pixel the navigation does not have — and on a short window
  // that showed up as Pricing and Billing clipped under the fold.
  const limit = limitOf(entitlements, "posts_per_month");
  const used = usedOf(entitlements, "posts_per_month");
  const pct = limit && limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;

  return (
    <nav
      ref={ref}
      id="app-sidebar"
      tabIndex={drawer ? -1 : undefined}
      className={drawer ? "app-sidebar drawer" : "app-sidebar"}
      aria-label="Main"
      onClick={(e) => {
        // Arriving somewhere closes the drawer; pressing the plan card or a
        // heading does not.
        if (drawer && (e.target as HTMLElement).closest("a")) onNavigate();
      }}
    >
      {/* The way out, on the thing being put away.
          There are three controls for one state now — this, the rail's, and the
          header's top-right one — and that is deliberate rather than
          redundant: the reach is different from each place, and the one people
          look for when a panel is in their way is on the panel. */}
      {/* A row of its own only in the drawer, where it carries the brand as
          well — and in the case where a role can see no navigation at all, so
          the control never disappears with the list. Otherwise it rides on the
          first group's heading rather than costing a row above it. */}
      {(drawer || groups.length === 0) && (
        <div className="sidebar-top">
          {drawer && (
            <Link to={DASHBOARD_ROUTE} className="sidebar-brand" title={`${COMPANY.product} — dashboard`}>
              <BrandMark />
              <span>{COMPANY.product}</span>
            </Link>
          )}
          <span className="spacer" />
          <CollapseButton drawer={drawer} onClose={onClose} />
        </div>
      )}

      <div className="sidebar-scroll">
        {groups.map((group, i) => (
          <div key={group.id} className="nav-group" role="group" aria-label={group.label}>
            <div className="nav-group-head">
              <h2 className="nav-group-label">{group.label}</h2>
              {/* On the first heading, where there was already a row of white
                  space at the top of this panel doing nothing else. */}
              {i === 0 && !drawer && <CollapseButton drawer={false} onClose={onClose} />}
            </div>
            <ul className="nav-list">
              {group.items.map((item) => (
                <Row key={item.id} item={item} />
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="sidebar-foot">
        {plan && (
          <div className="plan-card-nav">
            <Link to="/plans" className="plan-card-name" title="Plans, what each includes, and how many accounts each allows.">
              {plan.charAt(0).toUpperCase() + plan.slice(1)} plan
            </Link>
            {limit !== null && (
              <>
                <p className="muted plan-card-usage">
                  {used.toLocaleString()} / {formatLimit(limit, "posts")} this month
                </p>
                <span className="bar" role="img"
                      aria-label={`${used} of ${limit} posts used this month`}>
                  <span style={{ width: `${pct}%` }} className={pct >= 100 ? "full" : undefined} />
                </span>
              </>
            )}
            <Link to="/plans" className="plan-card-cta">
              {plan === "free" ? "Upgrade" : "See plans"} →
            </Link>
          </div>
        )}

        <ul className="nav-list">
          {NAV_FOOTER.map((item) => (
            <Row key={item.id} item={item} />
          ))}
        </ul>
      </div>
    </nav>
  );
}
